// Utilitas import CSV transaksi per buku kas.
// Berisi parser CSV ringan (RFC 4180) + normalisasi + validasi + deteksi duplikat.

export const MAX_ROWS = 1000;

// Pemetaan header CSV -> field internal (case-insensitive, trim).
const HEADER_MAP = {
  tanggal: 'date',
  date: 'date',
  waktu: 'time',
  time: 'time',
  jenis: 'type',
  type: 'type',
  jumlah: 'amount',
  nominal: 'amount',
  amount: 'amount',
  keterangan: 'note',
  catatan: 'note',
  note: 'note',
  kategori: 'category',
  category: 'category',
  dompet: 'pocket',
  wallet: 'pocket',
  pocket: 'pocket',
};

/**
 * Parse teks CSV menjadi array of array (baris -> kolom).
 * Menangani quote ("" escaping) dan newline di dalam quote.
 */
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;

  // Buang BOM jika ada.
  const src = text.replace(/^\uFEFF/, '');

  for (let i = 0; i < src.length; i++) {
    const char = src[i];

    if (inQuotes) {
      if (char === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (char === '\r') {
      // abaikan CR (CRLF)
    } else {
      field += char;
    }
  }

  // sisa baris terakhir
  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  return rows;
};

const isBlankRow = (cells) => cells.every((c) => (c || '').trim() === '');

/**
 * Normalisasi jenis transaksi ke 'debit'/'kredit'. Return null jika invalid.
 */
export const normalizeType = (value) => {
  const v = (value || '').toString().trim().toLowerCase();
  const debit = ['debit', 'masuk', 'pemasukan', 'income'];
  const kredit = ['kredit', 'keluar', 'pengeluaran', 'expense'];
  if (debit.includes(v)) return 'debit';
  if (kredit.includes(v)) return 'kredit';
  return null;
};

/**
 * Normalisasi tanggal ke YYYY-MM-DD. Menerima YYYY-MM-DD atau DD/MM/YYYY.
 * Return null jika invalid.
 */
export const normalizeDate = (value) => {
  const v = (value || '').toString().trim();
  if (v === '') return null;

  let y, m, d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    [y, m, d] = v.split('-');
  } else {
    const match = v.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
    if (!match) return null;
    d = match[1];
    m = match[2];
    y = match[3];
  }

  y = parseInt(y, 10);
  m = parseInt(m, 10);
  d = parseInt(d, 10);

  if (m < 1 || m > 12 || d < 1 || d > 31) return null;

  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return null;
  }

  const pad = (n) => String(n).padStart(2, '0');
  return `${y}-${pad(m)}-${pad(d)}`;
};

/**
 * Normalisasi waktu ke HH:mm. Kosong -> '00:00'. Return null jika invalid.
 */
export const normalizeTime = (value) => {
  const v = (value || '').toString().trim();
  if (v === '') return '00:00';
  const match = v.match(/^(\d{1,2})[:.-](\d{1,2})$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const i = parseInt(match[2], 10);
  if (h < 0 || h > 23 || i < 0 || i > 59) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(i)}`;
};

/**
 * Normalisasi jumlah: buang simbol Rp, spasi, dan pemisah ribuan.
 * Return int atau null.
 */
export const normalizeAmount = (value) => {
  let s = (value || '').toString().trim();
  s = s.replace(/[^0-9,.-]/g, '');
  if (s === '') return null;

  const hasComma = s.indexOf(',') !== -1;
  const hasDot = s.indexOf('.') !== -1;

  if (hasComma && hasDot) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    const parts = s.split(',');
    if (parts.length === 2 && parts[1].length === 3) {
      s = s.replace(/,/g, '');
    } else {
      s = s.replace(',', '.');
    }
  } else if (hasDot) {
    const parts = s.split('.');
    if (parts.length > 1 && parts[1].length === 3) {
      s = s.replace(/\./g, '');
    }
  }

  const num = Number(s);
  if (!Number.isFinite(num)) return null;
  return Math.round(num);
};

/**
 * Kunci duplikat transaksi.
 */
export const dupeKey = (date, type, amount, note, categoryId) => {
  return [
    (date || '').toString().slice(0, 10),
    (type || '').toString().toLowerCase(),
    String(parseInt(amount, 10) || ''),
    (note || '').toString().trim().toLowerCase(),
    (categoryId || '').toString(),
  ].join('|');
};

// Panjang note lama di DB (varchar(50)). Dipakai untuk toleransi data lama yang
// terpotong saat kolom note masih 50 karakter.
const LEGACY_NOTE_LEN = 50;

/**
 * Kunci duplikat "versi pendek": note dipotong 50 karakter.
 * Menangkap kasus data lama yang tersimpan terpotong (varchar(50)) sehingga tetap
 * terdeteksi duplikat walau teks CSV lebih panjang.
 */
export const dupeKeyShort = (date, type, amount, note, categoryId) => {
  return [
    (date || '').toString().slice(0, 10),
    (type || '').toString().toLowerCase(),
    String(parseInt(amount, 10) || ''),
    (note || '').toString().trim().toLowerCase().slice(0, LEGACY_NOTE_LEN),
    (categoryId || '').toString(),
  ].join('|');
};

/**
 * Hitung status satu baris: 'ok' | 'fix' | 'error' | 'dup'.
 */
export const computeStatus = (row) => {
  if (row.date === null) return 'error';
  if (row.time === null) return 'error';
  if (row.type === null) return 'error';
  if (row.amount === null || row.amount <= 0) return 'error';
  const noteLen = (row.note || '').trim().length;
  if (noteLen < 3 || noteLen > 255) return 'error';
  if (!row.categoryId) return 'fix';
  return 'ok';
};

/**
 * Terapkan deteksi duplikat ke seluruh baris.
 * Baris 'ok' yang kuncinya sama dengan baris sebelumnya (atau data existing di
 * DB) ditandai 'dup'.
 *
 * @param {Array} rows hasil buildPreviewRows
 * @param {Array} existingKeys array kunci dari data existing di DB
 */
export const applyDuplicateDetection = (rows, existingKeys = []) => {
  const seen = {};
  existingKeys.forEach((k) => {
    seen[k] = true;
  });

  return rows.map((row) => {
    if (row.status !== 'ok') return row;

    const key = dupeKey(row.date, row.type, row.amount, row.note, row.categoryId);
    const keyShort = dupeKeyShort(row.date, row.type, row.amount, row.note, row.categoryId);
    if (seen[key] || seen[keyShort]) {
      return { ...row, status: 'dup', reason: 'duplikat' };
    }
    seen[key] = true;
    seen[keyShort] = true;
    return row;
  });
};

/**
 * Ubah CSV text menjadi baris preview.
 *
 * @param {string} text isi file CSV
 * @param {object} opts { categories, pockets } daftar item existing book ini
 * @returns { rows, headerError }
 */
export const buildPreviewRows = (text, { categories = [], pockets = [] } = {}) => {
  const table = parseCsv(text);
  if (table.length === 0) {
    return { rows: [], headerError: 'File kosong.' };
  }

  // Cari baris header (lewati baris kosong & komentar '#').
  let headerIndex = -1;
  let headerCells = null;
  for (let i = 0; i < table.length; i++) {
    const cells = table[i];
    if (isBlankRow(cells)) continue;
    const first = (cells[0] || '').trim();
    if (first.startsWith('#')) continue;
    headerIndex = i;
    headerCells = cells.map((c) => (c || '').trim().toLowerCase());
    break;
  }

  if (headerIndex === -1 || !headerCells) {
    return { rows: [], headerError: 'Header tidak ditemukan.' };
  }

  // Petakan index kolom -> field internal.
  const colMap = {};
  headerCells.forEach((name, idx) => {
    const field = HEADER_MAP[name];
    if (field && colMap[field] === undefined) colMap[field] = idx;
  });

  const required = ['date', 'type', 'amount', 'note'];
  const missing = required.filter((f) => colMap[f] === undefined);
  if (missing.length > 0) {
    return {
      rows: [],
      headerError: `Kolom wajib tidak ditemukan: ${missing.join(', ')}.`,
    };
  }

  const catByName = {};
  categories.forEach((c) => {
    catByName[(c.title || '').trim().toLowerCase()] = c;
  });
  const pocketByName = {};
  pockets.forEach((p) => {
    pocketByName[(p.title || '').trim().toLowerCase()] = p;
  });

  const rows = [];
  for (let i = headerIndex + 1; i < table.length; i++) {
    const cells = table[i];
    if (isBlankRow(cells)) continue;
    const first = (cells[0] || '').trim();
    if (first.startsWith('#')) continue;

    const get = (field) => {
      const idx = colMap[field];
      return idx === undefined ? '' : (cells[idx] || '').trim();
    };

    const rawCategory = get('category');
    const rawPocket = get('pocket');

    const matchedCategory = rawCategory ? catByName[rawCategory.toLowerCase()] : null;
    const matchedPocket = rawPocket ? pocketByName[rawPocket.toLowerCase()] : null;

    const parsed = {
      row: i + 1, // nomor baris di file (1-based, termasuk header)
      raw: {
        date: get('date'),
        time: get('time'),
        type: get('type'),
        amount: get('amount'),
        note: get('note'),
        category: rawCategory,
        pocket: rawPocket,
      },
      date: normalizeDate(get('date')),
      time: normalizeTime(get('time')),
      type: normalizeType(get('type')),
      amount: normalizeAmount(get('amount')),
      note: get('note'),
      categoryId: matchedCategory ? matchedCategory.id : '',
      pocketId: matchedPocket ? matchedPocket.id : '',
      // Kategori/dompet yang tidak dikenali (belum ada di book ini)
      unknownCategory: rawCategory && !matchedCategory ? rawCategory : '',
      unknownPocket: rawPocket && !matchedPocket ? rawPocket : '',
      status: 'ok',
      reason: '',
    };

    parsed.status = computeStatus(parsed);
    rows.push(parsed);
  }

  return { rows, headerError: '' };
};
